import { useCallback, useState } from "react";
import { cn } from "../lib/cn";
import { Icon } from "./icon";

export type StepState = "done" | "current" | "todo";

export interface UseStepperResult {
  index: number;
  count: number;
  isFirst: boolean;
  isLast: boolean;
  next: () => void;
  prev: () => void;
  goTo: (index: number) => void;
  stateOf: (index: number) => StepState;
}

function clamp(value: number, count: number): number {
  return Math.min(Math.max(value, 0), Math.max(count - 1, 0));
}

/** Step index state (`[data-steps]` in app.js). Scrolls to top on change unless disabled. */
export function useStepper(count: number, options: { initial?: number; scrollToTop?: boolean } = {}): UseStepperResult {
  const { initial = 0, scrollToTop = true } = options;
  const [index, setIndex] = useState(() => clamp(initial, count));

  const goTo = useCallback(
    (target: number) => {
      setIndex(clamp(target, count));
      if (scrollToTop && typeof window.scrollTo === "function") {
        const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        try {
          window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
        } catch {
          // jsdom and some embedded browsers do not implement scrollTo; position is cosmetic.
        }
      }
    },
    [count, scrollToTop],
  );

  return {
    index,
    count,
    isFirst: index === 0,
    isLast: index === count - 1,
    next: () => {
      goTo(index + 1);
    },
    prev: () => {
      goTo(index - 1);
    },
    goTo,
    stateOf: (i) => (i < index ? "done" : i === index ? "current" : "todo"),
  };
}

export interface StepperStep {
  id: string;
  label: string;
}

export interface StepperProps {
  steps: readonly StepperStep[];
  current: number;
  /** When set, step indicators are buttons that jump to that step. */
  onStepClick?: (index: number) => void;
  "aria-label": string;
  className?: string;
  /** Visually hidden suffix for completed steps, e.g. "(completed)". */
  doneLabel?: string;
}

function stepState(i: number, current: number): StepState {
  return i < current ? "done" : i === current ? "current" : "todo";
}

function StepBadge({ n }: { n: number }) {
  return (
    <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-pill border border-line-strong bg-surface text-xs font-bold text-fg-muted group-data-[state=current]:border-primary group-data-[state=current]:bg-primary group-data-[state=current]:text-on-primary group-data-[state=done]:border-primary group-data-[state=done]:bg-primary-soft group-data-[state=done]:text-primary-ink">
      <span className="group-data-[state=done]:hidden">{n}</span>
      <Icon name="check" className="hidden size-3.5 group-data-[state=done]:inline-block" />
    </span>
  );
}

/** Onboarding-style progress indicator (`ol > li[data-state]`). */
export function Stepper({ steps, current, onStepClick, className, doneLabel = "(completed)", ...rest }: StepperProps) {
  return (
    <ol aria-label={rest["aria-label"]} className={cn("flex items-center", className)}>
      {steps.map((step, i) => {
        const state = stepState(i, current);
        const last = i === steps.length - 1;
        const content = (
          <>
            <StepBadge n={i + 1} />
            <span className="hidden text-[13px] font-semibold text-fg-muted group-data-[state=current]:text-fg md:block">
              {step.label}
            </span>
            {/* Labels hide < md; keep the name available to assistive tech. */}
            <span className="sr-only md:hidden">{step.label}</span>
            {state === "done" ? <span className="sr-only"> {doneLabel}</span> : null}
          </>
        );
        return (
          <li key={step.id} data-state={state} className={cn("group flex items-center", !last && "flex-1")}>
            {onStepClick ? (
              <button
                type="button"
                aria-current={state === "current" ? "step" : undefined}
                onClick={() => {
                  onStepClick(i);
                }}
                className="flex items-center gap-2 rounded-md py-1 pr-2 text-left"
              >
                {content}
              </button>
            ) : (
              <span aria-current={state === "current" ? "step" : undefined} className="flex items-center gap-2 py-1 pr-2">
                {content}
              </span>
            )}
            {last ? null : <span aria-hidden="true" className="mx-1.5 h-px flex-1 bg-line-strong group-data-[state=done]:bg-primary" />}
          </li>
        );
      })}
    </ol>
  );
}
